import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Building, 
  FileText, 
  Download, 
  Edit, 
  Save, 
  X, 
  Camera,
  Linkedin,
  Globe,
  Shield,
  Award,
  Star,
  Clock,
  Briefcase,
  Home,
  Smartphone,
  Mail as MailIcon,
  Phone as PhoneIcon,
  MapPin as MapPinIcon,
  Calendar as CalendarIcon,
  Building as BuildingIcon,
  FileText as FileTextIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  X as XIcon,
  Camera as CameraIcon,
  Linkedin as LinkedinIcon,
  Globe as GlobeIcon,
  Shield as ShieldIcon,
  Award as AwardIcon,
  Star as StarIcon,
  Clock as ClockIcon,
  Briefcase as BriefcaseIcon,
  Home as HomeIcon,
  Smartphone as SmartphoneIcon
} from 'lucide-react';

const Profile = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);

  // Datos del perfil (en un caso real vendrían de una API)
  const [profileData, setProfileData] = useState({
    personal: {
      firstName: 'Juan',
      lastName: 'Sepúlveda',
      email: 'juan.sepulveda@innovadev.com',
      phone: '+54 11 1234-5678',
      phoneSecondary: '+54 11 9876-5432',
      dateOfBirth: '1990-05-15',
      nationality: 'Argentina',
      maritalStatus: 'Soltero'
    },
    professional: {
      employeeId: 'INN-001',
      position: 'Senior Full Stack Developer',
      department: 'Desarrollo',
      role: 'Desarrollador',
      hireDate: '2020-03-15',
      contractType: 'Indefinido',
      salary: '$2,500 USD',
      supervisor: 'Diego Fernández',
      skills: ['React', 'Node.js', 'Python', 'AWS', 'Docker', 'MongoDB', 'PostgreSQL', 'TypeScript'],
      certifications: ['AWS Certified Developer', 'MongoDB Certified Developer', 'React Advanced Certification'],
      projects: ['E-commerce Platform', 'CRM System', 'Mobile App', 'API Gateway'],
      achievements: ['Employee of the Month - Q2 2023', 'Best Code Quality Award 2022', 'Innovation Prize 2021']
    },
    address: {
      street: 'Av. Corrientes 1234',
      city: 'Buenos Aires',
      state: 'Buenos Aires',
      zipCode: 'C1043',
      country: 'Argentina',
      addressType: 'Residencial'
    },
    emergency: {
      contact: 'María Sepúlveda',
      relationship: 'Madre',
      phone: '+54 11 1234-9999',
      email: 'maria.sepulveda@email.com'
    },
    documents: [
      {
        id: 1,
        name: 'LDA.pdf',
        type: 'application/pdf',
        size: '2.4 MB',
        uploadDate: '2024-01-15',
        category: 'Contrato',
        description: 'Liquidación de Deuda Administrativa'
      },
      {
        id: 2,
        name: 'CV_Actualizado.pdf',
        type: 'application/pdf',
        size: '1.8 MB',
        uploadDate: '2024-01-10',
        category: 'CV',
        description: 'Currículum Vitae actualizado'
      },
      {
        id: 3,
        name: 'DNI_Frente.pdf',
        type: 'image/jpeg',
        size: '856 KB',
        uploadDate: '2023-12-20',
        category: 'Identificación',
        description: 'Documento Nacional de Identidad - Frente'
      },
      {
        id: 4,
        name: 'DNI_Dorso.pdf',
        type: 'image/jpeg',
        size: '789 KB',
        uploadDate: '2023-12-20',
        category: 'Identificación',
        description: 'Documento Nacional de Identidad - Dorso'
      }
    ]
  });

  // Estado para datos editables
  const [editableData, setEditableData] = useState({
    personal: { ...profileData.personal },
    contact: { ...profileData.personal },
    address: { ...profileData.address }
  });

  // Función para manejar cambios en datos editables
  const handleInputChange = (section, field, value) => {
    setEditableData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Función para guardar cambios
  const handleSave = (section) => {
    setProfileData(prev => ({
      ...prev,
      [section]: { ...editableData[section] }
    }));
    
    if (section === 'personal') {
      setIsEditing(false);
    } else if (section === 'contact') {
      setIsEditingContact(false);
    } else if (section === 'address') {
      setIsEditingAddress(false);
    }

    // Aquí se enviarían los datos a la API
    console.log('Datos guardados:', editableData[section]);
  };

  // Función para cancelar edición
  const handleCancel = (section) => {
    setEditableData(prev => ({
      ...prev,
      [section]: { ...profileData[section] }
    }));
    
    if (section === 'personal') {
      setIsEditing(false);
    } else if (section === 'contact') {
      setIsEditingContact(false);
    } else if (section === 'address') {
      setIsEditingAddress(false);
    }
  };

  // Función para descargar archivo
  const handleDownload = (file) => {
    // Simular descarga
    const link = document.createElement('a');
    link.href = `#${file.name}`;
    link.download = file.name;
    link.click();
    console.log(`Descargando: ${file.name}`);
  };

  // Función para subir archivo
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newFile = {
        id: Date.now(),
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        uploadDate: new Date().toISOString().split('T')[0],
        category: 'Otros',
        description: 'Archivo subido por el usuario'
      };
      
      setProfileData(prev => ({
        ...prev,
        documents: [...prev.documents, newFile]
      }));
      
      setShowFileUpload(false);
      setSelectedFile(null);
    }
  };

  // Función para eliminar archivo
  const handleDeleteFile = (fileId) => {
    setProfileData(prev => ({
      ...prev,
      documents: prev.documents.filter(doc => doc.id !== fileId)
    }));
  };

  // Función para obtener ícono por tipo de archivo
  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return FileTextIcon;
    if (fileType.includes('image')) return CameraIcon;
    return FileTextIcon;
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto">
        {/* Header del perfil */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold gradient-text mb-2">
                Mi Perfil 👤
              </h1>
              <p className="text-text-secondary text-lg">
                Gestiona tu información personal y profesional
              </p>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              {isEditing ? <XIcon className="w-4 h-4" /> : <EditIcon className="w-4 h-4" />}
              <span>{isEditing ? 'Cancelar' : 'Editar Perfil'}</span>
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Columna izquierda - Información principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Información personal */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-text">Información Personal</h2>
                {isEditing && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleSave('personal')}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <SaveIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleCancel('personal')}
                      className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nombre
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editableData.personal.firstName}
                      onChange={(e) => handleInputChange('personal', 'firstName', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Apellido
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editableData.personal.lastName}
                      onChange={(e) => handleInputChange('personal', 'lastName', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Email
                  </label>
                  <p className="text-text font-medium">{profileData.personal.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Teléfono Principal
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editableData.personal.phone}
                      onChange={(e) => handleInputChange('personal', 'phone', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Fecha de Nacimiento
                  </label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editableData.personal.dateOfBirth}
                      onChange={(e) => handleInputChange('personal', 'dateOfBirth', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{formatDate(profileData.personal.dateOfBirth)}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nacionalidad
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editableData.personal.nationality}
                      onChange={(e) => handleInputChange('personal', 'nationality', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.nationality}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Información profesional */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <h2 className="text-2xl font-bold text-text mb-6">Información Profesional</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    ID de Empleado
                  </label>
                  <p className="text-text font-medium">{profileData.professional.employeeId}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Cargo
                  </label>
                  <p className="text-text font-medium">{profileData.professional.position}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Departamento
                  </label>
                  <p className="text-text font-medium">{profileData.professional.department}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Rol
                  </label>
                  <p className="text-text font-medium">{profileData.professional.role}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Fecha de Contratación
                  </label>
                  <p className="text-text font-medium">{formatDate(profileData.professional.hireDate)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Tipo de Contrato
                  </label>
                  <p className="text-text font-medium">{profileData.professional.contractType}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Supervisor
                  </label>
                  <p className="text-text font-medium">{profileData.professional.supervisor}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Salario
                  </label>
                  <p className="text-text font-medium">{profileData.professional.salary}</p>
                </div>
              </div>

              {/* Habilidades */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-text mb-3">Habilidades Técnicas</h3>
                <div className="flex flex-wrap gap-2">
                  {profileData.professional.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Certificaciones */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-text mb-3">Certificaciones</h3>
                <div className="space-y-2">
                  {profileData.professional.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <AwardIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-text-secondary">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Logros */}
              <div>
                <h3 className="text-lg font-semibold text-text mb-3">Logros y Reconocimientos</h3>
                <div className="space-y-2">
                  {profileData.professional.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <StarIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-text-secondary">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Archivos y Documentos */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-text">Archivos y Documentos</h2>
                <button
                  onClick={() => setShowFileUpload(!showFileUpload)}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Subir Archivo
                </button>
              </div>

              {/* Upload de archivos */}
              <AnimatePresence>
                {showFileUpload && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                  >
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        className="flex-1 px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text"
                      />
                      <button
                        onClick={() => setShowFileUpload(false)}
                        className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Lista de archivos */}
              <div className="space-y-3">
                {profileData.documents.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-4 bg-surface border border-gray-700 rounded-lg hover:border-primary transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        {(() => {
                          const FileIcon = getFileIcon(file.type);
                          return <FileIcon className="w-5 h-5 text-primary" />;
                        })()}
                      </div>
                      <div>
                        <h4 className="text-text font-medium">{file.name}</h4>
                        <p className="text-text-secondary text-sm">{file.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary">
                          <span>{file.category}</span>
                          <span>{file.size}</span>
                          <span>Subido: {formatDate(file.uploadDate)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDownload(file)}
                        className="p-2 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                        title="Descargar"
                      >
                        <DownloadIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Columna derecha - Información adicional */}
          <div className="space-y-6">
            {/* Datos de contacto */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-text">Datos de Contacto</h2>
                <button
                  onClick={() => setIsEditingContact(!isEditingContact)}
                  className="p-2 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                >
                  <EditIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Email
                  </label>
                  {isEditingContact ? (
                    <input
                      type="email"
                      value={editableData.contact.email}
                      onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Teléfono Principal
                  </label>
                  {isEditingContact ? (
                    <input
                      type="tel"
                      value={editableData.contact.phone}
                      onChange={(e) => handleInputChange('contact', 'phone', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Teléfono Secundario
                  </label>
                  {isEditingContact ? (
                    <input
                      type="tel"
                      value={editableData.contact.phoneSecondary}
                      onChange={(e) => handleInputChange('contact', 'phoneSecondary', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.personal.phoneSecondary}</p>
                  )}
                </div>

                {isEditingContact && (
                  <div className="flex space-x-2 pt-2">
                    <button
                      onClick={() => handleSave('contact')}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Guardar
                    </button>
                    <button
                      onClick={() => handleCancel('contact')}
                      className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Domicilio */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-text">Domicilio</h2>
                <button
                  onClick={() => setIsEditingAddress(!isEditingAddress)}
                  className="p-2 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                >
                  <EditIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Calle y Número
                  </label>
                  {isEditingAddress ? (
                    <input
                      type="text"
                      value={editableData.address.street}
                      onChange={(e) => handleInputChange('address', 'street', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <p className="text-text font-medium">{profileData.address.street}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Ciudad
                    </label>
                    {isEditingAddress ? (
                      <input
                        type="text"
                        value={editableData.address.city}
                        onChange={(e) => handleInputChange('address', 'city', e.target.value)}
                        className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                      />
                    ) : (
                      <p className="text-text font-medium">{profileData.address.city}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Provincia
                    </label>
                    {isEditingAddress ? (
                      <input
                        type="text"
                        value={editableData.address.state}
                        onChange={(e) => handleInputChange('address', 'state', e.target.value)}
                        className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                      />
                    ) : (
                      <p className="text-text font-medium">{profileData.address.state}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Código Postal
                    </label>
                    {isEditingAddress ? (
                      <input
                        type="text"
                        value={editableData.address.zipCode}
                        onChange={(e) => handleInputChange('address', 'zipCode', e.target.value)}
                        className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                      />
                    ) : (
                      <p className="text-text font-medium">{profileData.address.zipCode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      País
                    </label>
                    {isEditingAddress ? (
                      <input
                        type="text"
                        value={editableData.address.country}
                        onChange={(e) => handleInputChange('address', 'country', e.target.value)}
                        className="w-full px-3 py-2 bg-surface border border-gray-600 rounded-lg text-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                      />
                    ) : (
                      <p className="text-text font-medium">{profileData.address.country}</p>
                    )}
                  </div>
                </div>

                {isEditingAddress && (
                  <div className="flex space-x-2 pt-2">
                    <button
                      onClick={() => handleSave('address')}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Guardar
                    </button>
                    <button
                      onClick={() => handleCancel('address')}
                      className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Contacto de emergencia */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="card"
            >
              <h2 className="text-xl font-bold text-text mb-6">Contacto de Emergencia</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Nombre
                  </label>
                  <p className="text-text font-medium">{profileData.emergency.contact}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Relación
                  </label>
                  <p className="text-text font-medium">{profileData.emergency.relationship}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Teléfono
                  </label>
                  <p className="text-text font-medium">{profileData.emergency.phone}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Email
                  </label>
                  <p className="text-text font-medium">{profileData.emergency.email}</p>
                </div>
              </div>
            </motion.div>

            {/* Estadísticas del perfil */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="card"
            >
              <h2 className="text-xl font-bold text-text mb-6">Estadísticas del Perfil</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Documentos</span>
                  <span className="text-text font-medium">{profileData.documents.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Habilidades</span>
                  <span className="text-text font-medium">{profileData.professional.skills.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Certificaciones</span>
                  <span className="text-text font-medium">{profileData.professional.certifications.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Proyectos</span>
                  <span className="text-text font-medium">{profileData.professional.projects.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Logros</span>
                  <span className="text-text font-medium">{profileData.professional.achievements.length}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
